// Neo-soul (docs/AUDIO_DESIGN.md §12): D'Angelo / Dilla-era neo-soul through a lofi tape. A Wurlitzer (reedy, a bark that
// grows with velocity, the amp's tremolo on some tracks, a slight chorus) plays lush five-note voicings, a left-hand root
// under an upper structure of 9ths, 11ths and 13ths, on soulful changes with chromatic planing (the same shape sliding a half
// step); a warm fingered bass sits behind the beat, slides into chord tones and walks in chromatically; the drums are lazy
// and "drunk" (the kick a touch early, the snare late and wide, loose hats with ghost notes, a deep swing); a breathy "ooh"
// voice or a mellow synth sings laid-back phrases.
import { rng } from '../../engine/core.js';
import { QUALITIES, bassNote } from '../harmony.js';
import { createWurli, createSoulBass, createSoulKit, createSoulLead } from './neosoul-voices.js';

const mod12 = n => ((n % 12) + 12) % 12;
const ENERGY = { chill: 0, balanced: 1, upbeat: 2 };
const pickW = (r, w) => {
  let u = r() * Object.values(w).reduce((a, b) => a + b, 0);
  for (const [k, v] of Object.entries(w)) if ((u -= v) < 0) return k;
  return Object.keys(w)[0];
};

/* ---------------- voicings: a left-hand root under a four-note upper structure ---------------- */
// Upper tones (semitones over the root): the quality's tones without the root; more than 4 drop the 5th, fewer add the 11th
// on minor 7ths and half-diminished chords (diatonic on ii, iii and vi, the neo-soul m11) and the 9th elsewhere. Every order
// of them is stacked upward (each the nearest above the last) from MIDI 53–66: top 62–77, span ≤ 16, no minor 2nd between
// neighbours, at most one major 2nd (the cluster rub). The left hand plays the root at MIDI 43–54, a 4th or more under the
// right hand (a 5th under MIDI 48).
const upperOf = q => {
  const iv = QUALITIES[q] || QUALITIES.maj7;
  let up = [...new Set(iv.map(i => i % 12))].filter(i => i);
  if (up.length > 4 && up.includes(7)) up = up.filter(i => i !== 7);
  while (up.length > 4) up.pop();
  if (up.length === 3 && up.includes(10) && (up.includes(3) || q === 'm7b5')) up.push(5);
  else if (up.length === 3 && !up.some(i => i === 1 || i === 2 || (i === 3 && up.includes(4)))) up.push(2);   // no 9th yet
  return up;
};
const perms = a => (a.length <= 1 ? [a] : a.flatMap((x, i) => perms([...a.slice(0, i), ...a.slice(i + 1)]).map(p => [x, ...p])));
const SHAPES = new Map();                                   // `${root}:${q}` → [{ v, u, b, top, mean, m2 }]
const META = new WeakMap();                                 // a voicing → `${root}:${q}`, so the same chord keeps it
function shapes(root, q) {
  const key = `${root}:${q}`;
  if (SHAPES.has(key)) return SHAPES.get(key);
  const up = upperOf(q), b = 43 + mod12(root - 43), out = [], seen = new Set();
  const above = (m, iv) => m + 1 + mod12(root + iv - m - 1);
  for (const pm of perms(up)) for (let u0 = 53 + mod12(root + pm[0] - 53); u0 <= 66; u0 += 12) {
    const u = [u0];
    for (let i = 1; i < pm.length; i++) u.push(above(u[i - 1], pm[i]));
    const top = u[u.length - 1], gaps = u.slice(1).map((x, i) => x - u[i]), id = u.join();
    if (top < 62 || top > 77 || top - u0 > 16 || gaps.some(g => g < 2) || gaps.filter(g => g === 2).length > 1 || seen.has(id)) continue;
    if (u0 - b < (b < 48 ? 7 : 5)) continue;
    seen.add(id);
    const v = [b, ...u];
    META.set(v, key);
    out.push({ v, u, b, top, mean: u.reduce((s, x) => s + x, 0) / u.length, m2: gaps.filter(g => g === 2).length });
  }
  SHAPES.set(key, out);
  return out;
}
/** movement between upper structures (different sizes: each note to the nearest of the other, +1) */
function move(u, pu) {
  if (u.length === pu.length) return u.reduce((s, x, i) => s + Math.abs(x - pu[i]), 0);
  const near = (x, a) => Math.min(...a.map(y => Math.abs(x - y)));
  return (u.reduce((s, x) => s + near(x, pu), 0) + pu.reduce((s, y) => s + near(y, u), 0)) / 2 + 1;
}
/** The same chord keeps its voicing. Planing: a chord a half step from the last one, of the same quality, slides the whole
 *  shape (where it stays in range). Else the cheapest shape: Σ|Δupper| + .35·|Δroot| + 2·max(0, |Δtop| − 3) +
 *  .35·|mean(upper) − 64| + .8·max(0, top − 74) + .3 per major 2nd; the track's first chord .6·|mean − (64 + register)| +
 *  .5·|top − (70 + register)| + .3 per major 2nd instead (plan.register −5..+5: tracks don't all open on the same notes). */
export function soulVoicing(sp, prev, c, plan) {
  const root = mod12(plan.key.tonic + c.root), key = `${root}:${c.q}`, cands = shapes(root, c.q), reg = plan.register || 0;
  const pk = prev && META.get(prev);
  if (pk === key) return prev;
  if (pk) {
    const [pr, pq] = pk.split(':'), d = mod12(root - +pr);
    if (pq === c.q && (d === 1 || d === 11)) {
      const s = d === 1 ? 1 : -1, w = prev.map(m => m + s);
      if (w[0] >= 43 && w[0] <= 54 && w[1] >= 53 && w[w.length - 1] <= 77) { META.set(w, key); return w; }
    }
  }
  if (!cands.length) {
    const w = [43 + mod12(root - 43), ...sp.iv.map(i => 55 + mod12(sp.root + i - 55)).sort((a, b) => a - b)];
    META.set(w, key);
    return w;
  }
  let best = cands[0], bc = Infinity;
  for (const x of cands) {
    let k = 0.3 * x.m2;
    if (prev && prev.length > 1) {
      const pu = prev.slice(1);
      k += move(x.u, pu) + 0.35 * Math.abs(x.b - prev[0]) + 2 * Math.max(0, Math.abs(x.top - pu[pu.length - 1]) - 3)
        + 0.35 * Math.abs(x.mean - 64) + 0.8 * Math.max(0, x.top - 74);
    } else k += 0.6 * Math.abs(x.mean - 64 - reg) + 0.5 * Math.abs(x.top - 70 - reg);
    if (k < bc) { bc = k; best = x; }
  }
  return best.v;
}

/* ---------------- bass: melodic cells, a root on every change, chromatic walks in, slides ---------------- */
// One letter a 16th: R root · 5 fifth · 3 third (a tenth from the low roots) · 7 the 7th (6th) · o octave · . rest;
// 'kick' follows the kick grid. A track has a main cell and a second (on 15 % of bars, 60 % of phrase ends).
const BASS_CELLS = {
  hold: 'R...............',
  lay:  'R.........5.....',
  walk: 'R.......3...5...',
  pino: 'R......o.5......',
  soul: 'R.....7.R.......',
  sync: 'R..R......5..R..',
};
const BASS_POOL = [
  { hold: 0.8, lay: 1.2, walk: 0.6, kick: 0.5 },
  { lay: 1, walk: 0.8, pino: 1, soul: 0.8, kick: 0.6 },
  { pino: 1, soul: 1, sync: 1, walk: 0.6, kick: 0.6 },
];
/** One bar of bass (the main groove; intro, break and outro keep the default held roots). Before a new root: a chromatic
 *  approach on step 14 or 15 (from below 65 %), or a walk (a whole step, then a half step below it on 12 · 14); the root
 *  after it slides in (p .45). Colour notes slide (plan.ns.bass.slide). */
function soulBass({ plan, st, b, mode, chords, nextChords, chordAt, kicks, stop }) {
  if (mode !== 'kick') { st.nsInto = false; return null; }
  const B = plan.ns.bass, r = st.rS, pc = c => mod12(plan.key.tonic + c.root), into = !!st.nsInto;
  st.nsInto = false;
  const id = b.j % 4 === 3 ? B.cells[r() < 0.6 ? 1 : 0] : B.cells[r() < 0.85 ? 0 : 1];
  let hits = id === 'kick' ? kicks.map(s => [s, 'R']) : [...BASS_CELLS[id]].map((c, s) => [s, c]).filter(([, c]) => c !== '.');
  for (const c of chords) { const s0 = c.start * 4; hits = hits.filter(([s]) => s !== s0); hits.push([s0, 'R']); }
  hits.sort((x, y) => x[0] - y[0]);
  const last = chords[chords.length - 1], nx = nextChords && nextChords[0];
  if (nx && pc(nx) !== pc(last) && r() < B.approach) {
    const walk = r() < B.walk, s = walk ? 12 : r() < 0.6 ? 14 : 15;
    hits = hits.filter(([h]) => h < s);
    if (walk) hits.push([12, 'W'], [14, 'A']); else hits.push([s, 'A']);
  }
  if (stop != null) hits = hits.filter(([s]) => s < stop);
  const end = stop ?? 16, below = r() < 0.65;
  return hits.map(([s, c], k) => {
    const ch = chordAt(s), root = bassNote(pc(ch), st.prevBass), iv = QUALITIES[ch.q] || [0, 4, 7];
    let midi = root, len = (k + 1 < hits.length ? hits[k + 1][0] : end) - s, vel = s === 0 ? 0.9 : s % 4 ? 0.74 : 0.82, slide = false;
    if (c === 'A' || c === 'W') {
      const tg = bassNote(pc(nx), root);
      midi = c === 'W' ? tg - 2 : below || hits.some(h => h[1] === 'W') ? tg - 1 : tg + 1;
      vel = 0.7; len = Math.min(len, 2);
      st.nsInto = true;
    } else if (c === 'R') {
      if (s === 0 && into) slide = r() < 0.45;
      st.prevBass = root;
      len *= plan.bass.factor;
    } else {
      const third = iv.includes(3) ? 3 : iv.includes(4) ? 4 : 5, fifth = iv.includes(7) ? 7 : iv.includes(6) ? 6 : 7;
      const sev = iv.includes(10) ? 10 : iv.includes(11) ? 11 : iv.includes(9) ? 9 : 12;
      midi = c === '5' ? (root + fifth <= 52 ? root + fifth : root + fifth - 12)
        : c === '3' ? root + third + (root < 40 ? 12 : 0) : c === '7' ? root + sev : root + 12;
      vel = c === 'o' ? 0.76 : 0.72; slide = r() < B.slide;
      len *= plan.bass.factor;
    }
    return { s, midi, vel, dur: Math.max(0.5, len), slide };
  });
}

/* ---------------- melody: laid-back, syncopated phrases that sigh downwards (in place of the jazzhop motif) ---------------- */
// Rhythm cells (16th steps over 2 bars) that start late and push across the beat; legato gaps (p .6) glide in the voice.
// The contour steps down (p .6 to start), two steps (p .25) or turns back (p .15), turning round past ±3 scale steps; the
// last note holds 6–10 steps. The engine still picks the notes (the key's pentatonic, chord tones on beats 1 and 3).
const SCELLS = [[2, 4, 7, 10], [3, 6, 8, 14], [0, 3, 6, 11, 14], [2, 7, 10, 12], [1, 4, 8, 11], [3, 7, 12, 14], [0, 2, 3, 6, 10], [2, 6, 9, 16]];
const STAILS = [[], [18], [16, 19], [20], [19, 22]];
function soulMotif(r, key) {
  const steps = [...new Set(SCELLS[Math.floor(r() * SCELLS.length)].concat(STAILS[Math.floor(r() * STAILS.length)]))].sort((a, b) => a - b).slice(0, 6);
  const n = steps.length, last = 6 + Math.floor(r() * 5);
  const durs = steps.map((s, i) => (i === n - 1 ? last : r() < 0.6 ? Math.min(6, steps[i + 1] - s) : Math.max(1, Math.min(5, steps[i + 1] - s - 1))));
  const contour = [0];
  let dir = r() < 0.6 ? -1 : 1;
  for (let i = 1; i < n; i++) {
    const u = r(), mv = u < 0.6 ? dir : u < 0.85 ? 2 * dir : -dir;
    contour.push(contour[i - 1] + mv);
    if (Math.abs(contour[i]) >= 3) dir = -Math.sign(contour[i]);
  }
  const vel = steps.map((s, i) => (i === 0 ? 0.74 : i === n - 1 ? 0.7 : +(0.6 + 0.1 * r()).toFixed(3)));
  return { steps, durs, contour, vel, mode: key.mode };
}

/* ---------------- comping: Wurlitzer cells of [step, length, velocity, pick] ---------------- */
// Voicing indices: 0 = the left-hand root, 1–4 = the right hand (-1 = the top). A chord struck again lets its old notes go.
const LH = [0], RH = [1, 2, 3, 4], TOP2 = [-2, -1], TOP3 = [-3, -2, -1];
const COMPS = {
  hold:  [[0, 16, 0.92]],
  lay:   [[1, 9, 0.92], [10, 6, 0.7, RH]],                                        // a 16th behind the beat, then the right hand
  dilla: [[0, 3, 0.95], [6, 4, 0.72], [10, 6, 0.85]],                             // short, short, long
  split: [[0, 16, 0.86, LH], [2, 14, 0.84, RH], [11, 5, 0.6, TOP2]],              // the left hand, the right an 8th later
  stab:  [[0, 2, 0.95], [3, 3, 0.7, RH], [8, 2, 0.9], [11, 5, 0.75, RH]],
  ghost: [[0, 10, 0.9], [10, 2, 0.45, TOP3], [14, 2, 0.55, TOP3]],               // held, then soft upper taps
  roll:  [[0, 16, 0.88, [0, 1]], [1, 15, 0.74, [2]], [2, 14, 0.76, [3]], [3, 13, 0.82, [4]]],   // a slow gospel roll up
  pulse: [[0, 6, 0.9], [8, 4, 0.8], [12, 4, 0.7, RH]],
};

/* ---------------- drums: lazy, drunk, deep swing ---------------- */
const GRIDS = {
  //          0123456789abcdef
  drunk:  { K: 'x......x.x......', S: '....x.......x...', H: 'x.xsx.xsx.xsx.xs' },                   // Dilla: a kick on the 2-and, a 16th late on 3
  voodoo: { K: 'x.......x.x.....', S: '....x..g....x..g', H: 'x.x.x.x.x.x.x.x.' },                   // ghost snares into 3 and 1
  slump:  { K: 'x..x......x..x..', S: '....x.......x...', H: 'x.x.xsx.x.x.xsx.' },                   // a pushed kick, 16th pickups
  lay:    { K: 'x.........x.....', S: '....x.......x...', R: '..s.......s.....', H: 'x.x.x.x.x.x.x.x.' }, // boom-bap, cross-stick taps
  rim:    { K: 'x......x..x.....', R: '....x.......x...', H: 'x.xsx.x.x.xsx.x.' },                   // a cross-stick backbeat
  half:   { K: 'x.....x.......x.', S: '........x.......', R: '....s.......s..s', H: 'x.x.x.x.x.x.x.x.' }, // halftime
  break:  { K: 'x.........x.....', R: '....x.......x...', H: 'x...x...x...x...' },
};

export default {
  id: 'neosoul', name: 'Neo-soul', desc: 'Wurlitzer, lush extended chords and lazy drums behind the beat',
  //            chill                                    balanced                                  upbeat
  vary: 'jazz',
  energy: {
    bpm:        [[68, 76],                               [74, 84],                                 [80, 92]],
    swing:      [[60, 66],                               [58, 64],                                 [56, 62]],
    snareLag:   [[0.022, 0.034],                         [0.018, 0.03],                            [0.012, 0.024]],
    groove:     [[{ half: 1.3, rim: 1.2, lay: 1, voodoo: 0.4 }, { rim: 1, lay: 1.1, voodoo: 0.8, drunk: 0.4 }],
                 [{ voodoo: 1, lay: 1, drunk: 0.8, rim: 0.6 }, { voodoo: 1.2, drunk: 1.1, lay: 0.8, slump: 0.6, rim: 0.4 }],
                 [{ drunk: 1.2, slump: 1, voodoo: 1, lay: 0.4 }, { drunk: 1.2, slump: 1, voodoo: 1, lay: 0.4 }]],
    stretch:    [0.45,                                   0.3,                                      0.18],
    drumLevel:  [0.8,                                    0.9,                                      0.95],
    kitLp:      [[4600, 6400],                           [5400, 7600],                             [6200, 8600]],
    epLp:       [[2600, 3400],                           [3000, 4000],                             [3400, 4600]],   // the Wurlitzer's speaker LP
    strum:      [[0.014, 0.03],                          [0.01, 0.022],                            [0.007, 0.016]],
    epVel:      [[0.5, 0.6],                             [0.54, 0.64],                             [0.54, 0.65]],
    comp:       [{ hold: [0.8, 1.4], roll: [0.4, 0.9], lay: [0.4, 0.8], split: [0.3, 0.7], ghost: [0.1, 0.4], pulse: [0, 0.2], dilla: [0, 0.2], stab: [0, 0.1] },
                 { hold: [0.2, 0.6], roll: [0.2, 0.5], lay: [0.5, 1], split: [0.3, 0.7], ghost: [0.3, 0.7], pulse: [0.2, 0.6], dilla: [0.4, 0.9], stab: [0.1, 0.4] },
                 { hold: [0, 0.2], roll: [0.1, 0.3], lay: [0.3, 0.7], split: [0.2, 0.5], ghost: [0.4, 0.8], pulse: [0.4, 0.8], dilla: [0.6, 1.1], stab: [0.4, 0.9] }],
    push:       [0.18,                                   0.3,                                      0.4],
    legato:     [0.9,                                    0.8,                                      0.65],
    lead:       [0.5,                                    0.62,                                     0.72],
    softLead:   [0.65,                                   0.6,                                      0.5],            // 'soft' sings the "ooh", else the mellow synth
    tone:       [[4200, 6200],                           [5000, 7600],                             [6000, 8600]],
    wowCents:   [[6, 11],                                [5, 9],                                   [4, 8]],
    dust:       [[0.9, 1.5],                             [0.7, 1.3],                               [0.6, 1.1]],
  },
  // ns13sus = V13sus4 (4 ♭7 9 13 over the root)
  qualities: { ns13sus: [0, 5, 10, 14, 21] },
  progressions: {
    major: {
      n1: [[5, 'maj9', 4], [4, 'm9', 2], [3, 'm9', 2], [2, 'm9', 4], [0, 'maj9', 4]],                // IVmaj9 · iii9|♭iii9 · ii9 · Imaj9 (planing)
      n2: [[2, 'm9', 4], [7, 'ns13sus', 4], [0, 'maj9', 4], [0, '69', 3], [3, 'm9', 1]],               // ii9 · V13sus · Imaj9 · I6/9|♭iii9 (slides into ii9)
      n3: [[8, 'maj9', 4], [10, '13', 4], [0, 'maj9', 7], [9, 'maj9', 1]],                             // ♭VImaj9 · ♭VII13 · Imaj9 · …|VImaj9 (into ♭VI)
      n4: [[2, 'm11', 4], [1, '13', 4], [0, 'maj9', 4], [9, 'm9', 4]],                                 // ii11 · ♭II13 (tritone sub) · Imaj9 · vi9
      n5: [[5, 'maj9', 4], [5, 'm9', 4], [4, 'm7', 4], [9, '7s9', 4]],                                 // IVmaj9 · iv9 · iii11 · VI7♯9
      n6: [[0, 'maj9', 4], [4, 'm7', 4], [5, 'maj9', 4], [5, 'm9', 2], [10, '13', 2]],                 // Imaj9 · iii11 · IVmaj9 · iv9|♭VII13
    },
    minor: {
      s1: [[0, 'm9', 4], [5, 'm9', 4], [10, '13', 4], [3, 'maj9', 2], [8, 'maj9', 2]],                 // i9 · iv9 · ♭VII13 · ♭IIImaj9|♭VImaj9
      s2: [[8, 'maj9', 4], [10, '13', 4], [0, 'm11', 4], [0, 'm9', 3], [9, 'maj9', 1]],                // ♭VImaj9 · ♭VII13 · i11 · i9|VImaj9 (into ♭VI)
      s3: [[0, 'm11', 4], [5, '9', 4], [0, 'm11', 4], [5, '9', 2], [1, 'm11', 2]],                     // i11 · IV9 · i11 · IV9|♭ii11 (planing into i)
      s4: [[2, 'm7b5', 4], [7, '7s9', 4], [0, 'm9', 4], [0, 'm11', 4]],                                // iiø11 · V7♯9 · i9 · i11
      s5: [[0, 'm9', 4], [8, 'maj9', 4], [5, 'm9', 4], [7, '7s9', 3], [1, 'm9', 1]],                   // i9 · ♭VImaj9 · iv9 · V7♯9|♭ii9 (into i9)
      s6: [[5, 'm9', 4], [10, '13', 4], [3, 'maj9', 4], [8, 'maj9', 2], [7, '7s9', 2]],                // iv9 · ♭VII13 · ♭IIImaj9 · ♭VImaj9|V7♯9
    },
  },
  turnarounds: { major: [[2, 'm9', 2], [7, 'ns13sus', 2]], minor: [[2, 'm7b5', 2], [7, '7s9', 2]] },   // ii9|V13sus · iiø|V7♯9
  grids: GRIDS,
  fills: {
    ghost: { roll: [[12, 0.3], [13, 0.38], [14, 0.48], [15, 0.6]] },                                   // a ghost-note crescendo
    drag:  { roll: [[13, 0.32], [14, 0.78]] },
    kicks: { kicks: [13, 15] },
    open:  { open: 14 },
    stop:  { stop: 12 },
  },
  comps: COMPS,
  keysLevel: 1.55,                                                  // Chords only: +3.8 dB (the Wurlitzer alone sits 3–4 dB under the band)
  plan(plan, { r, energy }) {
    const e = ENERGY[energy] ?? 1, rn = (a, b, d = 3) => +(a + r() * (b - a)).toFixed(d);
    const trem = r() < [0.55, 0.45, 0.35][e] ? { rate: rn(5.4, 6.3, 2), depth: rn(0.1, 0.22) } : null;
    plan.ns = {
      wurli: { trem, chorus: { rate: rn(0.35, 0.65, 2), cents: rn(3, 5, 1) }, drive: rn(...[[0.8, 0.95], [0.88, 1.04], [0.96, 1.12]][e], 2) },
      bass: { cells: [pickW(r, BASS_POOL[e]), pickW(r, BASS_POOL[e])], approach: [0.4, 0.5, 0.55][e], walk: [0.15, 0.25, 0.3][e],
        slide: rn(0.25, 0.4, 2), lag: rn(0.006, 0.014, 4), lp: rn(340, 440, 0) },
      lead: { vib: rn(10, 16, 1), glide: rn(0.04, 0.07), scoop: rn(0.3, 0.55, 2) },
    };
    plan.drums.kit = {
      kickF0: rn(105, 125, 1), kickF1: rn(45, 51, 1), kickDecay: rn(0.17, 0.24), early: rn(0.006, 0.014, 4),
      snareF: rn(1500, 2000, 0), snareDecay: rn(0.1, 0.15), wide: rn(0.25, 0.4, 2), hatHP: rn(5000, 6400, 0), hatClosed: rn(0.016, 0.026),
      loose: rn(0.003, 0.008, 4), rimF: rn(480, 560, 0), lp: plan.drums.kit.lp,
    };
    if (plan.lead) {
      plan.lead.voice = plan.lead.timbre === 'soft' ? 'ooh' : 'mellow';
      plan.lead.motif = soulMotif(r, plan.key);
    }
  },
  voicing: soulVoicing,
  bass: soulBass,
  voices(ac, dry, wet, plan, ctx) {
    const o = { registry: ctx.registry, tag: ctx.tag, t0: ctx.t0 }, sub = () => rng(Math.floor(ctx.r() * 4294967296));
    return {
      ep: createWurli(ac, dry, wet, { lp: plan.ep.lp, ...plan.ns.wurli }, { ...o, cap: ctx.cap, r: sub(), log: ctx.log }),
      bass: createSoulBass(ac, dry, plan.ns.bass, o),
      kit: createSoulKit(ac, dry, wet, plan.drums.kit, { ...o, stepDur: ctx.stepDur }),
      lead: plan.lead ? createSoulLead(ac, dry, wet, plan.lead, plan.ns.lead, { ...o, stepDur: ctx.stepDur, r: sub() }) : null,
    };
  },
};
